import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = mdoc => mdoc.id;

export const memberDocumentSlice = createSlice({
    name: 'memberDocs',
    initialState: {
        data: {},
    },
    reducers: {
        set: (state, action) => {
            state.data[action.payload.memberid] = action.payload.data; 
        },
        addOrUpdate: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byMember = groupBy(toUpdate, 'memberid');
            Object.entries(byMember).forEach(([ memberid, docs ]) => {
                const ids = new Set(docs.map(d => key(d)));
                state.data[memberid] = ((state.data[memberid]?.filter(mdoc => !ids.has(key(mdoc)))) ?? []).concat(docs);
            });
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byMember = groupBy(toRemove, 'memberid');
            Object.entries(byMember).forEach(([ memberid, docs ]) => {
                const ids = new Set(docs.map(d => key(d)));
                if (state.data[memberid]) {
                    state.data[memberid] = state.data[memberid].filter(mdoc => !ids.has(key(mdoc)));
                    if (state.data[memberid].length === 0) {
                        delete state.data[memberid];
                    }
                }
            });
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => memberDocumentSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'staffDocs', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'staffDocs');

const fetchMemberDocs = memberid => network => network.get('/api/v1/memberDocs/' + encodeURIComponent(memberid), data => memberDocumentSlice.actions.set({ memberid, data}), visualizeFailure('get'));
export const fetchMemberDoc = async (network, id) => network.fetchData('/api/v1/memberDoc/' + encodeURIComponent(id));
export const addMemberDoc = (network, doc) => {
    let data = new FormData();
    data.append('memberid', doc.memberid)
    data.append('file', doc.file);
    data.append('filename', doc.filename);
    data.append('member_accessible', doc.member_accessible);
    return network.postData('/api/v1/memberDoc', data)
};
export const updateMemberDoc = (network, data) => network.put('/api/v1/memberDoc', data, memberDocumentSlice.addOrUpdate, visualizeFailure('update'));
export const removeMemberDoc = (network, id) => network.delete(`/api/v1/memberDoc/${encodeURIComponent(id)}`, memberDocumentSlice.actions.remove, visualizeFailure('remove'));

// #endregion

// #region selectors
const selectAllMemberDocData = state => state.memberDocs.data;
const selectMemberDocs = memberid => createSelector([selectAllMemberDocData], docs => docs[memberid] ?? []);
// #endregion

const listen = {
    memberAdminDocCreated: (doc, { dispatch, websocket }) => {
        markFetched(websocket, 'member_doc', key(doc))
        dispatch(memberDocumentSlice.actions.addOrUpdate(doc))
    },
    memberAdminDocRemoved: (doc, { dispatch }) => dispatch(memberDocumentSlice.actions.remove(doc)),
    memberStaffDocCreated: (doc, { dispatch }) => dispatch(memberDocumentSlice.actions.addOrUpdate(doc)),
    memberStaffDocRemoved: (doc, { dispatch }) => dispatch(memberDocumentSlice.actions.remove(doc))
}

const memberInitDocsByID = initMapFactory(fetchMemberDocs, listen)
registerEntityBasedFetch('member', memberInitDocsByID);

const fullRoles = ['administrator', 'training manager', 'member manager'];

const checkPermissions = (credentials, member, requiredRoles, condition) => {
    const access = credentials.hasRole(requiredRoles, {office: member?.office, client: member?.default_client, location: member?.default_location});
    return (access && condition !== false && !member?.inactive) ?? false 
}

const cleanup = { root: memberDocumentSlice.name, all: state => Object.values(selectAllMemberDocData(state)).flat(), removeAll: memberDocumentSlice.actions.remove, key };
export const [useMemberDocs] = dataHookFactory(selectMemberDocs, memberInitDocsByID, {cleanup});

export const canUpdateMemberDocs = (credentials, member) => checkPermissions(credentials, member, fullRoles, !member.inactive);

const reducer = { name: memberDocumentSlice.name, reducer: memberDocumentSlice.reducer }
export default reducer;